import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Mail, Clock, LogOut, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { authFetch } from "../utils/api";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_emergent-website-3/artifacts/6i1oj5oc_BTR_Health_favicon__2_-removebg-preview.png";

export default function WorkerPendingPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    document.title = "Application under review | BTR Health";
  }, []);

  const checkStatus = async () => {
    setChecking(true);
    try {
      const r = await authFetch(`${API}/worker/profile/me`);
      if (r.ok) {
        const data = await r.json();
        const status = data?.approval_status || data?.profile?.approval_status;
        if (status === "approved") {
          toast.success("You're approved! Redirecting…");
          setTimeout(() => { window.location.assign("/worker-dashboard"); }, 700);
          return;
        }
      }
      toast.message("Still under review — we'll email you the moment you're approved.");
    } catch (_) {
      toast.error("Couldn't check status. Try again in a minute.");
    } finally {
      setChecking(false);
    }
  };

  const name = (user?.name || "").split(" ")[0] || "there";

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-white flex flex-col">
      <header className="px-4 sm:px-6 py-3.5 flex items-center justify-between border-b border-slate-100 bg-white/80 backdrop-blur">
        <Link to="/" className="inline-flex items-center gap-2" data-testid="worker-pending-home">
          <img src={LOGO_ICON} alt="BTR Health" className="h-7 w-7" />
          <span className="font-bold text-base text-slate-900">BTR Health</span>
        </Link>
        <button
          onClick={() => { try { logout && logout(); } catch (_) {} window.location.assign("/"); }}
          className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5"
          data-testid="worker-pending-logout"
        >
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </header>

      <main className="flex-1 flex items-center justify-center p-5 sm:p-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="w-full max-w-lg"
        >
          <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/40 border border-slate-100 p-7 sm:p-9 text-center">
            {/* Icon */}
            <div className="mx-auto w-16 h-16 rounded-2xl bg-[#87CEEB]/15 flex items-center justify-center mb-5">
              <ShieldCheck className="w-7 h-7 text-[#87CEEB]" />
            </div>

            <h1 className="text-2xl sm:text-[28px] font-bold text-slate-900 tracking-tight">
              Thanks {name} — your profile is being verified.
            </h1>
            <p className="text-slate-500 text-sm sm:text-[15px] mt-2.5">
              Our team is reviewing your documents and credentials. We'll email you the moment you're approved (usually within 1–2 business days).
            </p>

            <div className="mt-7 space-y-3 text-left">
              {[
                { icon: Clock, title: "Under review", desc: "Police check, WWCC, NDIS clearance and insurance/ABN are being verified." },
                { icon: Mail, title: "We'll email you", desc: `A confirmation will land in ${user?.email || "your inbox"} as soon as your profile is approved.` },
                { icon: ShieldCheck, title: "What happens next", desc: "Once approved you'll appear in our directory and can accept shifts." },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="flex items-start gap-3 p-3.5 rounded-2xl bg-slate-50 border border-slate-100">
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-[#87CEEB]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{title}</p>
                    <p className="text-slate-500 text-[13px] leading-snug mt-0.5">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <Button
              onClick={checkStatus}
              disabled={checking}
              className="w-full h-12 rounded-xl bg-[#87CEEB] hover:bg-[#6BB8DB] text-white font-semibold mt-7"
              data-testid="worker-pending-check"
            >
              {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><RefreshCw className="w-4 h-4 mr-2" /> Check approval status</>)}
            </Button>

            <p className="text-[12px] text-slate-400 mt-4">
              Need help? Email{" "}
              <a href="mailto:hello@btrhealth.com.au" className="text-[#5BA3C0] hover:underline">hello@btrhealth.com.au</a>
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
